'use client';

import Link from '@eliadmin/core/src/components/link';

import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { CarouselWall } from '../ui/carousel-wall';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { ReferenceCard } from '../references/reference-card';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselProgress } from '../ui/carousel-progress';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { IoIosArrowForward } from 'react-icons/io';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function References({ references, title }: { references: Reference[]; title: string }) {
  const { t } = useTranslations();
  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section
      className={cn(
        'custom-section',
        'ea-section__xl-t pr-0',
        'ea-section__xxl-b sm:ea-section__xl-t',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 sm:flex-row sm:items-center',
        )}
      >
        <div className="relative z-20 flex w-full flex-col items-start justify-start gap-6 sm:min-w-[427px] sm:max-w-[427px]">
          <Heading title={title} tag="h2" className="w-full text-balance" />
          <Button asChild className="hidden sm:flex">
            <Link href={'/realizacie'}>
              {t('text819')}
            </Link>
          </Button>
        </div>
        <div className="relative flex w-full flex-col items-start justify-start gap-6 overflow-visible">
          <CarouselWall className="bg-[#fafafa]" />
          <Carousel
            options={options}
            className={{
              container: 'hide-scroll-bar w-full gap-4 overflow-visible sm:gap-5 lg:gap-6',
              main: 'hide-scroll-bar w-full overflow-visible',
              wrapper: 'hide-scroll-bar w-full overflow-visible',
              slide:
                'w-full max-w-[320px] sm:max-w-[360px] md:max-w-[470px] lg:max-w-[600px] xl:max-w-[664px]',
            }}
            slides={references.map(reference => (
              <ReferenceCard reference={reference} key={reference.slug} />
            ))}
          />
          <div className="flex w-full max-w-[877px] items-center justify-between gap-6 pr-5 sm:pr-0">
            <CarouselProgress selectedIndex={selectedIndex} totalSlides={references.length} />
            <CarouselControls
              buttonProps={{ variant: 'default', size: 'icon' }}
              className={{
                main: 'hidden min-w-[112px] max-w-[112px] sm:flex',
                button: 'border-none bg-[#F7EEDD] text-black shadow-none hover:bg-primary',
              }}
              nextIcon={<IoIosArrowForward size={20} />}
              prevIcon={<IoIosArrowForward size={20} className="rotate-180" />}
            />
          </div>
          <div className="flex w-full pr-5 sm:hidden">
            <Button asChild className="w-full">
              <Link href={'/realizacie'}>
                {t('text819')}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
